import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Flame, Trophy, Zap, Radio } from "lucide-react";

/**
 * HighlightsReel — 20-second auto-scrolling ticker on Public Home.
 * Reads /api/public/highlights and rotates through recent set-wins,
 * streaks and full-time results. Pauses on hover. Subscribes to public
 * SSE so newly-scored highlights slot in without a refresh.
 */
const ROTATE_MS = 4000;

const ICONS = {
  final: Trophy,
  set_won: Zap,
  streak: Flame,
};
const COLORS = {
  final: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  set_won: "text-[#F4B400] border-[#F4B400]/40 bg-[#F4B400]/10",
  streak: "text-rose-300 border-rose-500/40 bg-rose-500/10",
};

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function HighlightsReel() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/public/highlights?limit=30");
      setItems(data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const base = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
    const es = new EventSource(`${base}/api/public/events/stream`);
    const refresh = () => load();
    ["match.set_scored", "match.finalized", "match.point_scored"].forEach((e) => es.addEventListener(e, refresh));
    es.onerror = () => { };
    return () => es.close();
  }, []);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    timerRef.current = setTimeout(() => setIdx((i) => (i + 1) % items.length), ROTATE_MS);
    return () => clearTimeout(timerRef.current);
  }, [idx, items.length, paused]);

  if (items.length === 0) return null;
  const cur = items[idx % items.length];

  return (
    <div
      className="rounded-2xl border border-white/10 bg-gradient-to-r from-[#0a1611] via-[#0f1a15] to-[#0a1611] overflow-hidden"
      data-testid="highlights-reel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex items-center gap-3 px-4 py-2 border-b border-white/5 bg-black/30">
        <Radio className="w-3.5 h-3.5 text-red-400 animate-pulse" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">Highlights Reel · {items.length} moments</div>
        <div className="ml-auto flex gap-1">
          {items.slice(0, 8).map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`reel-dot-${i}`}
              className={`w-1.5 h-1.5 rounded-full ${i === (idx % Math.min(items.length, 8)) ? "bg-[#F4B400]" : "bg-white/20"}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
      <ReelCard cur={cur} key={idx} />
    </div>
  );
}

function ReelCard({ cur }) {
  if (!cur) return null;
  const Icon = ICONS[cur.type] || Flame;
  const cl = COLORS[cur.type] || COLORS.streak;
  const winnerA = cur.type === "final" ? cur.winner === "a" : cur.winner === "a" || cur.streak_team === "a";
  const winnerB = cur.type === "final" ? cur.winner === "b" : cur.winner === "b" || cur.streak_team === "b";

  return (
    <Link
      to={`/matches/${cur.match_id}`}
      className="block px-5 py-4 hover:bg-black/20 transition-colors"
      data-testid={`reel-card-${cur.type}-${cur.match_id}`}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded border ${cl}`}>
          <Icon className="w-3 h-3" /> {cur.label}
        </span>
        <span className="text-[10px] uppercase text-zinc-500">
          {cur.stage === "group" ? "League" : cur.stage === "quarter" ? "QF" : cur.stage === "semi" ? "SF" : cur.stage === "final" ? "Final" : cur.stage}
          {cur.seed ? ` · Seed ${cur.seed}` : ""}
        </span>
        <span className="text-[10px] text-zinc-500 ml-auto">{fmtTime(cur.at)}</span>
      </div>
      <div className="mt-2 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div className="flex items-center gap-2 justify-end min-w-0">
          <div className={`text-right font-anton uppercase text-xl leading-none truncate ${winnerA ? "text-white" : "text-zinc-400"}`}>{cur.team_a?.name}</div>
          <div className="w-8 h-8 rounded bg-black/40 overflow-hidden shrink-0">
            {cur.team_a?.logo_url && <img src={cur.team_a.logo_url} alt="" className="w-full h-full object-contain" />}
          </div>
        </div>
        <div className="font-anton text-4xl md:text-5xl text-white leading-none">
          {cur.type === "final" ? (
            <>{cur.score_a}<span className="text-zinc-500 mx-2">–</span>{cur.score_b}</>
          ) : cur.type === "set_won" ? (
            <>{cur.set_score_a}<span className="text-zinc-500 mx-2">–</span>{cur.set_score_b}</>
          ) : (
            <>{cur.score_a}<span className="text-zinc-500 mx-2">–</span>{cur.score_b}</>
          )}
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded bg-black/40 overflow-hidden shrink-0">
            {cur.team_b?.logo_url && <img src={cur.team_b.logo_url} alt="" className="w-full h-full object-contain" />}
          </div>
          <div className={`font-anton uppercase text-xl leading-none truncate ${winnerB ? "text-white" : "text-zinc-400"}`}>{cur.team_b?.name}</div>
        </div>
      </div>
    </Link>
  );
}
